import { TFunction } from 'i18next'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useResyncBuilding, useUpdateBuilding } from '@/api/building'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { popupFactory, toastFactory } from '@/factories'
import { Building } from '@/reducers/types'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/error'

import { PlaceFlags } from '../../features/places-features.constants'
import {
  mapPlaceSaveError,
  PlaceSaveError,
  PlaceSaveErrorKind,
} from '../../features/places-features.errors'
import { getFeatureWarnings, PlaceFeatureWarning } from '../../features/places-features.rules'
import { buildPlaceUpdatePayload } from '../../place-payload'
import { PlaceSaveWarnings } from './place-save-warnings'

export interface PlaceSaveParams {
  // Note: send JSON instead of multipart (see updateBuilding) - for array fields, no files
  json?: boolean
  // Note: the full next state of the flags this section edits, used to warn about side effects
  nextFlags?: PlaceFlags
  payload: Record<string, unknown>
}

export type PlaceSaveResult =
  | { building: Building; ok: true }
  | { error: PlaceSaveError; message: string; ok: false }
  | { ok: false }

const getSaveErrorMessage = (error: PlaceSaveError, t: TFunction<['organisation']>) => {
  switch (error.kind) {
    case PlaceSaveErrorKind.codeExists:
      return t('organisation:places.building.errors.codeExists')
    case PlaceSaveErrorKind.integrationNotConfigured:
      return t('organisation:places.save.errors.integrationNotConfigured', {
        provider: error.provider ?? '',
      })
    case PlaceSaveErrorKind.locationsExist:
      return t('organisation:places.save.errors.locationsExist')
    case PlaceSaveErrorKind.marketShopRestaurant:
      return t('organisation:places.save.errors.marketShopRestaurant')
    case PlaceSaveErrorKind.multipleTicketTypes:
      return t('organisation:places.save.errors.multipleTicketTypes')
    case PlaceSaveErrorKind.packageRoomMissing:
      return t('organisation:places.save.errors.packageRoomMissing')
    case PlaceSaveErrorKind.parentOff:
      return t('organisation:places.save.errors.parentOff')
    case PlaceSaveErrorKind.remoteLockParent:
      return t('organisation:places.save.errors.remoteLockParent')
    default:
      return error.message
  }
}

const confirmWarnings = (warnings: PlaceFeatureWarning[], t: TFunction<['organisation']>) =>
  new Promise<boolean>((resolve) => {
    popupFactory.open(ConfirmationPopup, {
      children: <PlaceSaveWarnings warnings={warnings} />,
      confirmText: t('organisation:places.save.warnings.confirm'),
      onClose: () => resolve(false),
      onConfirm: () => resolve(true),
      title: t('organisation:places.save.warnings.title'),
      variant: warnings.some(({ severity }) => severity === 'destructive') ? 'danger' : 'warning',
    })
  })

export const usePlaceSave = (building: Building) => {
  const { t } = useTranslation(['organisation'])
  const { mutateAsync: updateBuilding } = useUpdateBuilding()
  const resyncBuilding = useResyncBuilding()
  const [isSaving, setIsSaving] = useState(false)

  const save = useCallback(
    async ({ json, nextFlags, payload }: PlaceSaveParams): Promise<PlaceSaveResult> => {
      const warnings = nextFlags ? getFeatureWarnings(building, nextFlags) : []
      if (warnings.length && !(await confirmWarnings(warnings, t))) return { ok: false }

      setIsSaving(true)
      try {
        const updated = await updateBuilding({
          data: buildPlaceUpdatePayload({ scalars: payload, server: building }),
          id: building.id,
          json,
        })
        toastFactory.success(t('organisation:places.building.toasts.updated'))
        return { building: updated, ok: true }
      } catch (error) {
        const saveError = mapPlaceSaveError(
          getApiErrorMessage(error),
          getApiErrorStatus(error as Error),
        )
        const message = getSaveErrorMessage(saveError, t)
        // Note: a taken code is shown inline on the field by the caller
        if (saveError.kind !== PlaceSaveErrorKind.codeExists) toastFactory.error(message)
        // Note: the backend can persist part of an update before failing, so the store is re-synced
        await resyncBuilding(building.id).catch(() => undefined)
        return { error: saveError, message, ok: false }
      } finally {
        setIsSaving(false)
      }
    },
    [building, resyncBuilding, t, updateBuilding],
  )

  return { isSaving, save }
}
